// src/components/InviteModal.ts
// Generate household invites with configurable roles, shareable links, and QR codes

import QRCode from 'qrcode';
import type { Household, Invite } from '../types';
import { api } from '../services/api';
import { showToast } from './Toast';

export class InviteModal {
  private el: HTMLElement;
  private household: Household | null = null;
  private activeInvites: Invite[] = [];
  private currentCreatedInvite: Invite | null = null;

  constructor() {
    this.el = document.createElement('div');
    this.el.className = 'modal-overlay';
    document.body.appendChild(this.el);

    this.el.addEventListener('click', (e) => {
      if (e.target === this.el) this.close();
    });
  }

  public async open(household: Household) {
    this.household = household;
    this.currentCreatedInvite = null;
    this.render();
    this.el.classList.add('open');
    await this.loadInvites();
  }

  public close() {
    this.el.classList.remove('open');
  }

  private async loadInvites() {
    if (!this.household) return;
    try {
      const res = await api.getInvites(this.household.id);
      this.activeInvites = res.invites;
      this.render();
    } catch (err: any) {
      showToast('Failed to load invites: ' + err.message);
    }
  }

  private render() {
    if (!this.household) return;

    const joinBaseUrl = window.location.origin + '/join?code=';

    let generatedInviteHtml = '';
    if (this.currentCreatedInvite) {
      const joinUrl = joinBaseUrl + this.currentCreatedInvite.code;
      generatedInviteHtml = `
        <div style="background:var(--sage-light);border:1px solid var(--sage-border);border-radius:var(--radius-md);padding:1rem;margin-bottom:1rem;text-align:center;">
          <div style="font-size:0.85rem;font-weight:600;color:var(--sage);margin-bottom:0.25rem;">
            Invite Created for ${this.currentCreatedInvite.role.toUpperCase()}
          </div>
          <div class="invite-code-pill" style="display:inline-block;margin:0.5rem 0;">
            ${this.currentCreatedInvite.code}
          </div>

          <!-- QR Code Canvas -->
          <div class="qr-container">
            <canvas id="invite-qr-canvas"></canvas>
            <span style="font-size:0.75rem;color:var(--ink-secondary);">Scan with phone camera to join instantly</span>
          </div>

          <div style="display:flex;gap:0.5rem;margin-top:0.75rem;">
            <input type="text" class="input-field" readonly value="${joinUrl}" id="copy-join-input" style="font-size:0.8rem;flex:1;" />
            <button class="btn btn-primary" id="copy-join-btn">📋 Copy Link</button>
          </div>
        </div>
      `;
    }

    const pendingInvitesList = this.activeInvites.length
      ? this.activeInvites
          .map((inv) => {
            const exp = inv.expiresAt
              ? new Date(inv.expiresAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
              : 'Never';
            return `
              <div style="display:flex;align-items:center;justify-content:space-between;padding:0.5rem 0.75rem;background:var(--bg-subtle);border-radius:var(--radius-sm);font-size:0.82rem;">
                <div>
                  <span style="font-family:monospace;font-weight:700;">${inv.code}</span>
                  <span class="role-badge ${inv.role}" style="margin-left:0.35rem;">${inv.role}</span>
                  <span style="color:var(--ink-muted);font-size:0.75rem;margin-left:0.4rem;">Expires: ${exp}</span>
                </div>
                <button class="btn-icon revoke-invite-btn" data-id="${inv.id}" title="Revoke" style="width:26px;height:26px;color:var(--rose);">✕</button>
              </div>
            `;
          })
          .join('')
      : `<div style="font-size:0.8rem;color:var(--ink-muted);text-align:center;padding:0.5rem;">No active invite links</div>`;

    this.el.innerHTML = `
      <div class="modal-card">
        <div class="modal-header">
          <h2 class="modal-title">Invite to ${escapeHtml(this.household.name)}</h2>
          <button class="btn-icon" id="invite-close-btn">✕</button>
        </div>

        <div class="modal-body">
          <p style="font-size:0.88rem;color:var(--ink-secondary);">
            Invite family members or housemates. Choose what role they should have:
          </p>

          <div style="display:grid;grid-template-columns:1fr 1fr;gap:0.75rem;">
            <label style="display:flex;flex-direction:column;gap:0.3rem;background:var(--bg-subtle);border:1px solid var(--border);padding:0.75rem;border-radius:var(--radius-sm);cursor:pointer;">
              <div style="display:flex;align-items:center;gap:0.4rem;">
                <input type="radio" name="invite-role" value="shopper" checked />
                <span style="font-weight:600;font-size:0.9rem;">🛒 Shopper</span>
              </div>
              <span style="font-size:0.75rem;color:var(--ink-secondary);">Can view lists, tick off items in store & finish shops.</span>
            </label>

            <label style="display:flex;flex-direction:column;gap:0.3rem;background:var(--bg-subtle);border:1px solid var(--border);padding:0.75rem;border-radius:var(--radius-sm);cursor:pointer;">
              <div style="display:flex;align-items:center;gap:0.4rem;">
                <input type="radio" name="invite-role" value="editor" />
                <span style="font-weight:600;font-size:0.9rem;">📝 Editor</span>
              </div>
              <span style="font-size:0.75rem;color:var(--ink-secondary);">Can create & delete lists, add items, and upload meal plans.</span>
            </label>
          </div>

          <div style="display:flex;gap:0.5rem;align-items:center;margin-top:0.25rem;">
            <label style="font-size:0.85rem;color:var(--ink-secondary);white-space:nowrap;">Expires In:</label>
            <select class="input-field" id="invite-expiry-select" style="flex:1;">
              <option value="7">7 Days</option>
              <option value="30">30 Days</option>
              <option value="0">Never (Reusable)</option>
            </select>
          </div>

          <button class="btn btn-sage" id="generate-invite-submit-btn" style="width:100%;margin-top:0.25rem;">
            ✨ Generate Invite Link & QR Code
          </button>

          ${generatedInviteHtml}

          <!-- Pending Invites -->
          <div style="margin-top:0.75rem;">
            <div style="font-size:0.85rem;font-weight:600;margin-bottom:0.4rem;">Active Invites (${this.activeInvites.length})</div>
            <div style="display:flex;flex-direction:column;gap:0.4rem;">
              ${pendingInvitesList}
            </div>
          </div>
        </div>
      </div>
    `;

    // Events
    this.el.querySelector('#invite-close-btn')?.addEventListener('click', () => this.close());

    // Generate Submit
    this.el.querySelector('#generate-invite-submit-btn')?.addEventListener('click', async () => {
      const role = (this.el.querySelector('input[name="invite-role"]:checked') as HTMLInputElement)?.value as 'editor' | 'shopper' || 'shopper';
      const expiryDays = parseInt((this.el.querySelector('#invite-expiry-select') as HTMLSelectElement)?.value || '7');

      try {
        const res = await api.createInvite(this.household!.id, role, expiryDays, 0);
        this.currentCreatedInvite = res.invite;
        this.render();
        await this.renderQrCode();
        showToast('Invite link & QR code generated!');
      } catch (err: any) {
        showToast('Failed to create invite: ' + err.message);
      }
    });

    // Copy button
    this.el.querySelector('#copy-join-btn')?.addEventListener('click', () => {
      const input = this.el.querySelector('#copy-join-input') as HTMLInputElement;
      if (input) {
        navigator.clipboard.writeText(input.value);
        showToast('📋 Link copied to clipboard!');
      }
    });

    // Revoke buttons
    this.el.querySelectorAll('.revoke-invite-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.getAttribute('data-id')!;
        try {
          await api.revokeInvite(this.household!.id, id);
          showToast('Invite revoked');
          await this.loadInvites();
        } catch (err: any) {
          showToast('Error: ' + err.message);
        }
      });
    });

    if (this.currentCreatedInvite) {
      this.renderQrCode();
    }
  }

  private async renderQrCode() {
    if (!this.currentCreatedInvite) return;
    const canvas = this.el.querySelector('#invite-qr-canvas') as HTMLCanvasElement;
    if (!canvas) return;

    const joinUrl = window.location.origin + '/join?code=' + this.currentCreatedInvite.code;
    try {
      await QRCode.toCanvas(canvas, joinUrl, {
        width: 180,
        margin: 2,
        color: {
          dark: '#1A1814',
          light: '#FFFFFF',
        },
      });
    } catch (err) {
      console.error('QR code generation failed:', err);
    }
  }
}

function escapeHtml(s: string): string {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
